import classNames from 'classnames';
import Cookies from 'js-cookie';
import React, { useCallback, useState } from 'react';
import parser from 'react-html-parser';
import { connect } from 'react-redux';

import { Card, Cardcordion } from '@app/components/card';
import {
	PackageCardTabOverlay,
	ExamCardTabOverlay,
} from '@app/components/card-tab-overlay';
import { CardTabsList } from '@app/components/card-tabs';
import {
	PackageForm,
	ExamForm,
	NoWindowPackageForm,
	NoWindowExamForm,
} from '@app/components/product-forms';
import { ProductFamily } from '@app/constants/product-family';
import { useMergeState, useCardScroll } from '@app/hooks';
import { getCartIdByContact } from '@app/store/actions/cart.actions';
import { mapDispatchToPropsWithReset } from '@app/store/map-dispatch-to-props';
import { capitalize } from '@app/utility/capitalize';

import { Hr } from '../hr';
import { PdfIcon } from '../icons';

const tabsMap = {
	PACKAGES: 0,
	EXAMS: 1,
};

const CoursePanel = ({ children }) => (
	<div className="course-panel__wrapper">
		<div className="course-panel__container">
			<div className="course-panel__content">{children}</div>
		</div>
	</div>
);

const ProductCardRaw = ({
	auth,
	cart,
	form,
	index,
	item,
	hasExamWindows,
	handleFormReset,
	courseDocuments,
}) => {
	const initialState = {
		activeTab: tabsMap.PACKAGES,
		selectedPackageLabels: null,
		selectedExamLabels: null,
		isAccordionOpen: false,
		hasMatchingPackageItem: false,
		hasMatchingExamItem: false,
		packageExamWindow: null,
		examExamWindow: null,
	};
	const [state, mergeState] = useMergeState(initialState);
	const [cartId, setCartId] = useState(Cookies.get('WCSNUM'));
	const { isAuthenticated } = auth;

	const cardRef = React.useRef();

	const {
		activeTab,
		selectedPackageLabels,
		selectedExamLabels,
		isAccordionOpen,
		hasMatchingPackageItem,
		hasMatchingExamItem,
		packageExamWindow,
		examExamWindow,
	} = state;
	const { cartItems } = cart;
	const {
		id,
		title,
		abbreviation,
		longDescription,
		code,
		minEstimatedCompletionTime,
		maxEstimatedCompletionTime,
		estimatedCompletionUnits,
		topics,
		examWindows,
	} = item;

	const packageFormId = `package-form-${id}-${index}`;
	const packageFormSucceeded =
		form[packageFormId] && form[packageFormId].submitSucceeded;
	const examFormId = `exam-form-${id}-${index}`;
	const examFormSucceeded =
		form[examFormId] && form[examFormId].submitSucceeded;

	useCardScroll(cardRef, isAccordionOpen);

	// Check for cartId by contact if WCSNUM cookie is not set.
	const getCartData = useCallback(async () => {
		if (!Cookies.get('WCSNUM') && isAuthenticated) {
			setCartId(await getCartIdByContact());
		}
	}, [setCartId, isAuthenticated]);

	React.useEffect(() => {
		getCartData();
	}, [getCartData]);

	// #region Handle form submissions
	React.useEffect(() => {
		if (packageFormSucceeded) {
			mergeState({
				isPackageOverlayOpen: true,
				hasMatchingExamItem: cartItems.some(
					({ productFamily, courseCode }) =>
						(productFamily === ProductFamily.EXAMREGIST ||
							productFamily === ProductFamily.TOPIC_COURSE) &&
						courseCode === code,
				),
				packageExamWindow:
					form[packageFormId] &&
					form[packageFormId].values['package-exam-window'],
			});
		}

		if (examFormSucceeded) {
			mergeState({
				isExamOverlayOpen: true,
				hasMatchingPackageItem: cartItems.some(
					({ inventoryClass, courseCode }) =>
						inventoryClass === 'PK' && courseCode === code,
				),
				examExamWindow:
					form[examFormId] && form[examFormId].values['exam-exam-window'],
			});
		}
	}, [
		form,
		code,
		cartItems,
		packageFormId,
		examFormId,
		packageFormSucceeded,
		examFormSucceeded,
		handleFormReset,
		mergeState,
	]);

	const resetForm = (formType) => {
		handleFormReset(formType === 'package' ? packageFormId : examFormId);

		if (formType === 'package') {
			mergeState({
				isPackageOverlayOpen: false,
			});
		} else {
			mergeState({
				isExamOverlayOpen: false,
			});
		}
	};

	const handleAccordionToggle = () =>
		mergeState((prevState) => ({
			isAccordionOpen: !prevState.isAccordionOpen,
		}));

	const setSelectedPackageLabels = (labels) =>
		mergeState({
			selectedPackageLabels: labels,
		});

	const setSelectedExamLabels = (labels) =>
		mergeState({
			selectedExamLabels: labels,
		});

	const setActiveTab = (tab) => {
		resetForm(tab === 0 ? 'package' : 'exam');
		mergeState({ activeTab: tab });
	};

	const packageContentId = `package-content-panel-${id}-${index}`;
	const packageTabAriaLabel = `package-tab-${id}-${index}`;
	const examContentId = `exam-content-panel-${id}-${index}`;
	const examTabAriaLabel = `exam-tab-${id}-${index}`;
	const cardMoreInfoAriaControl = `card-more-info-${id}-${index}`;
	const completion = `${minEstimatedCompletionTime}-${maxEstimatedCompletionTime} ${capitalize(
		estimatedCompletionUnits,
	)}`;
	const tabs = [
		{
			type: 'package',
			id: packageContentId,
			title: 'Purchase Course Materials',
			ariaLabel: packageTabAriaLabel,
			onClick: () => mergeState({ activeTab: tabsMap.PACKAGES }),
		},
		{
			type: 'exam',
			id: examContentId,
			title: 'Purchase Course Exam',
			ariaLabel: examTabAriaLabel,
			onClick: () => mergeState({ activeTab: tabsMap.EXAMS }),
		},
	];

	const hasCoursePackages =
		examWindows &&
		examWindows.filter((examWindow) => {
			// Check for online packages.
			const onlinePackages =
				(examWindow.onlineCourses.length > 0 &&
					examWindow.onlineCourses.filter((i) => !i.courseCode)) ||
				[];

			return (
				examWindow.packages.length > 0 ||
				(examWindow.printedMaterialIds &&
					examWindow.printedMaterialIds.length > 0) ||
				(examWindow.printedMaterials &&
					examWindow.printedMaterials.length > 0) ||
				onlinePackages.length > 0
			);
		}).length > 0;
	const hasCourseExams =
		examWindows &&
		examWindows.filter((examWindow) => {
			// Check for online exams
			const onlineExams =
				(examWindow.onlineCourses.length > 0 &&
					examWindow.onlineCourses.filter((i) => !!i.courseCode)) ||
				[];

			return examWindow.exams.length > 0 || onlineExams.length > 0;
		}).length > 0;

	return (
		<Card ref={cardRef} className="u-margin-bottom">
			<p className="h4 u-margin-hug card__title">{`${abbreviation}: ${title}`}</p>

			<div className="card__text">{parser(longDescription)}</div>

			{courseDocuments &&
				courseDocuments.map(({ linkText = '', fileUrl = '' }) => (
					<a
						key={fileUrl}
						href={fileUrl}
						className="btn btn--text btn--icon btn--icon--left btn--icon--lg"
						target="_blank"
						rel="noopener noreferrer"
					>
						<span>
							<PdfIcon />
							{linkText}
						</span>
					</a>
				))}

			<Cardcordion
				isOpen={isAccordionOpen}
				onToggle={handleAccordionToggle}
				ariaControl={cardMoreInfoAriaControl}
			>
				<dl className="u-padding-top detail-list__container detail-list__container--4-col">
					<div className="detail-list__item">
						<dt className="detail-list__title">Topics</dt>
						<dd className="detail-list__desc">
							{topics && (
								<ul className="u-margin-hug">
									{topics.map((topic) => (
										<li key={topic}>{topic}</li>
									))}
								</ul>
							)}
						</dd>
					</div>

					<div className="detail-list__item">
						<dt className="detail-list__title">Language(s)</dt>
						<dd className="detail-list__desc">English</dd>
					</div>

					<div className="detail-list__item">
						<dt className="detail-list__title">Suggested Self-Study Time</dt>
						<dd className="detail-list__desc">{completion}</dd>
					</div>
				</dl>

				<Hr neutralLight />

				<h4 className="u-margin-hug-top">Get Started</h4>
				<p className="u-margin-hug-top">
					Purchase your course materials, exam, or both.
				</p>

				{/* Render only package form if course has package options but no exams */}
				{hasCoursePackages && !hasCourseExams && (
					<CoursePanel>
						{!hasExamWindows && (
							<NoWindowPackageForm
								form={packageFormId}
								formData={examWindows}
								onSubmit={setSelectedPackageLabels}
							/>
						)}

						{hasExamWindows && (
							<>
								<p className="u-margin-hug-top">
									Select the exam window you are targeting as well as your
									materials package.
								</p>

								<PackageForm
									form={packageFormId}
									formData={examWindows}
									onSubmit={setSelectedPackageLabels}
								/>
							</>
						)}

						<PackageCardTabOverlay
							auth={auth}
							show={packageFormSucceeded && cartId}
							labels={selectedPackageLabels}
							hasCourseExams={hasCourseExams}
							hasMatchingCartItem={hasMatchingExamItem}
							onClose={resetForm}
						/>
					</CoursePanel>
				)}

				{/* Render only exam form if course has exam options but no packages */}
				{!hasCoursePackages && hasCourseExams && (
					<CoursePanel>
						{!auth.isAuthenticated && (
							<>
								<p className="u-margin-hug-top">
									You must be logged in to your account to select your course
									exam.
								</p>

								<p className="u-margin-hug-top u-font-family-text">
									<strong>
										Sign in or create an account to purchase your exam.
									</strong>
								</p>
							</>
						)}

						{!hasExamWindows && (
							<NoWindowExamForm
								form={examFormId}
								formData={examWindows}
								courseCode={code}
								onSubmit={setSelectedExamLabels}
							/>
						)}

						{hasExamWindows && (
							<ExamForm
								form={examFormId}
								formData={examWindows}
								onSubmit={setSelectedExamLabels}
							/>
						)}

						<ExamCardTabOverlay
							show={examFormSucceeded && cartId}
							labels={selectedExamLabels}
							hasCoursePackages={hasCoursePackages}
							onClose={resetForm}
						/>
					</CoursePanel>
				)}

				{/* Render card tabs if course has package and exam options */}
				{hasCoursePackages && hasCourseExams && (
					<CardTabsList
						className="card-tabs__container--sm"
						activeTab={activeTab}
						tabs={tabs}
					>
						<div
							className={classNames('card-tabs__panel', {
								'is-active': activeTab === tabsMap.PACKAGES,
							})}
							id={packageContentId}
							role="tabpanel"
							aria-labelledby={packageTabAriaLabel}
						>
							{!hasExamWindows && (
								<NoWindowPackageForm
									form={packageFormId}
									formData={examWindows}
									onSubmit={setSelectedPackageLabels}
								/>
							)}

							{hasExamWindows && (
								<>
									<p className="u-margin-hug-top">
										Select exam window you are targeting as well as your
										materials package.
									</p>

									<PackageForm
										form={packageFormId}
										formData={examWindows}
										courseCode={code}
										examWindow={examExamWindow}
										onSubmit={setSelectedPackageLabels}
									/>
								</>
							)}
						</div>

						<PackageCardTabOverlay
							auth={auth}
							show={
								activeTab === tabsMap.PACKAGES && packageFormSucceeded && cartId
							}
							labels={selectedPackageLabels}
							hasCourseExams={hasCourseExams}
							hasMatchingCartItem={hasMatchingExamItem}
							onClick={setActiveTab}
							onClose={resetForm}
						/>

						<div
							className={classNames('card-tabs__panel', {
								'is-active': activeTab === tabsMap.EXAMS,
							})}
							id={examContentId}
							role="tabpanel"
							aria-labelledby={examTabAriaLabel}
						>
							{!auth.isAuthenticated && (
								<>
									<p className="u-margin-hug-top">
										You must be logged in to your account to select your course
										exam.
									</p>

									<p className="u-margin-hug-top u-font-family-text">
										<strong>
											Sign in or create an account to purchase your exam.
										</strong>
									</p>
								</>
							)}

							{!hasExamWindows && (
								<NoWindowExamForm
									form={examFormId}
									formData={examWindows}
									courseCode={code}
									onSubmit={setSelectedExamLabels}
								/>
							)}

							{hasExamWindows && (
								<ExamForm
									form={examFormId}
									formData={examWindows}
									courseCode={code}
									examWindow={packageExamWindow}
									onSubmit={setSelectedExamLabels}
								/>
							)}
						</div>

						<ExamCardTabOverlay
							show={activeTab === tabsMap.EXAMS && examFormSucceeded && cartId}
							labels={selectedExamLabels}
							hasCoursePackages={hasCoursePackages}
							hasMatchingCartItem={hasMatchingPackageItem}
							onClick={setActiveTab}
							onClose={resetForm}
						/>
					</CardTabsList>
				)}
			</Cardcordion>
		</Card>
	);
};

const mapStateToProps = ({ app, auth, cart, form }) => ({
	app,
	auth,
	cart,
	form,
});

export const ProductCard = connect(
	mapStateToProps,
	mapDispatchToPropsWithReset,
)(ProductCardRaw);
